import { SELECTED_TOKENS_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { Vault } from '@/types/vault'
import { getAvailableTokens } from '@/utils/tokens/getAvailableTokens'
import { getGroupedTokens } from '@/utils/tokens/getGroupedTokens'
import { getOverwrittenTokenSymbol } from '@/utils/tokens/getOverwrittenTokenSymbol'
import type { PopoverProps } from '@radix-ui/react-popover'
import { getTokenImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { AccordionItem } from '../common/accordion'
import { RoundImage } from '../common/roundImage'
import { FilterAccordionContent, FilterAccordionTrigger } from './filterSelect'
import { GroupedFilterSelect } from './groupedFilterSelect'
import { GroupedFilterListContent } from './groupedFilterSelectContent'

interface Props {
  vaults: Vault[]
  className?: string
}

function useTokenFilterData(vaults: Vault[]) {
  const { selectedTokens, updateSearchState } = useVaultsSearchState()
  const { trackEvent } = useMixpanel()
  const tokens = useMemo(
    () => getAvailableTokens(vaults).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
    [vaults]
  )
  const groupedItems = useMemo(() => {
    const { ethTokens, usdCoins, euroCoins, btcCoins, otherTokens } = getGroupedTokens(tokens)
    return {
      ETH: { items: ethTokens, isSelectable: true },
      USD: { items: usdCoins, isSelectable: true },
      EURO: { items: euroCoins, isSelectable: true },
      BTC: { items: btcCoins, isSelectable: true },
      Other: { items: otherTokens, isSelectable: false },
    }
  }, [tokens])

  return {
    groupedItems,
    selectedItems: selectedTokens,
    setSelectedItems: (value: string[]) => {
      updateSearchState([SELECTED_TOKENS_KEY, value])
      trackEvent('Filter used', { Filter: 'token', Value: value.join(', ') })
    },
    name: 'tokens',
    renderItemPrefix: (item: string) => (
      <RoundImage
        src={getTokenImage(item)}
        fallback={getTokenImage('fallback')}
        alt={`${item} token`}
        className="size-7"
      />
    ),
    searchNormalizer: getOverwrittenTokenSymbol,
  }
}

export function TokenFilter({ vaults, className, modal }: Props & Pick<PopoverProps, 'modal'>) {
  const props = useTokenFilterData(vaults)
  return <GroupedFilterSelect {...props} scrollClassName="max-h-56" modal={modal} className={className} />
}

export const TokenFilterAccordion = ({ vaults }: Props) => {
  const props = useTokenFilterData(vaults)
  return (
    <AccordionItem value="tokens">
      <FilterAccordionTrigger name="tokens" />
      <FilterAccordionContent>
        <GroupedFilterListContent {...props} scrollClassName="max-h-48" />
      </FilterAccordionContent>
    </AccordionItem>
  )
}
